function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { logger } from '@sentry/utils';
import { DEBUG_BUILD } from '../debug-build.js';
import { getMainCarrier } from '../hub.js';
import { spanToTraceHeader } from '../utils/spanUtils.js';
import { registerErrorInstrumentation } from './errors.js';
import { IdleTransaction } from './idletransaction.js';
import { sampleTransaction } from './sampling.js';
import { Transaction } from './transaction.js';
/** Returns all trace headers that are currently on the top scope. */

function traceHeaders() {
  // eslint-disable-next-line deprecation/deprecation
  var scope = this.getScope(); // eslint-disable-next-line deprecation/deprecation

  var span = scope.getSpan();
  return span ? {
    'sentry-trace': spanToTraceHeader(span)
  } : {};
}
/**
 * Creates a new transaction and adds a sampling decision if it doesn't yet have one.
 *
 * The Hub.startTransaction method delegates to this method to do its work, passing the Hub instance in as `this`, as if
 * it had been called on the hub directly. Exists as a separate function so that it can be injected into the class as an
 * "extension method."
 *
 * @param this: The Hub starting the transaction
 * @param transactionContext: Data used to configure the transaction
 * @param CustomSamplingContext: Optional data to be provided to the `tracesSampler` function (if any)
 *
 * @returns The new transaction
 *
 * @see {@link Hub.startTransaction}
 */


function _startTransaction(transactionContext, customSamplingContext) {
  // eslint-disable-next-line deprecation/deprecation
  var client = this.getClient();
  var options = client && client.getOptions() || {};
  var configInstrumenter = options.instrumenter || 'sentry';
  var transactionInstrumenter = transactionContext.instrumenter || 'sentry';

  if (configInstrumenter !== transactionInstrumenter) {
    DEBUG_BUILD && logger.error("A transaction was started with instrumenter=`".concat(transactionInstrumenter, "`, but the SDK is configured with the `").concat(configInstrumenter, "` instrumenter.\nThe transaction will not be sampled. Please use the ").concat(configInstrumenter, " instrumentation to start transactions.")); // eslint-disable-next-line deprecation/deprecation

    transactionContext.sampled = false;
  } // eslint-disable-next-line deprecation/deprecation


  var transaction = new Transaction(transactionContext, this);
  transaction = sampleTransaction(transaction, options, _objectSpread({
    name: transactionContext.name,
    parentSampled: transactionContext.parentSampled,
    transactionContext: transactionContext,
    attributes: _objectSpread(_objectSpread({}, transactionContext.data), transactionContext.attributes)
  }, customSamplingContext));

  if (transaction.isRecording()) {
    transaction.initSpanRecorder(options._experiments && options._experiments.maxSpans);
  }

  if (client && client.emit) {
    client.emit('startTransaction', transaction);
  }

  return transaction;
}
/**
 * Create new idle transaction.
 */


function startIdleTransaction(hub, transactionContext, idleTimeout, finalTimeout, onScope, customSamplingContext, heartbeatInterval) {
  var delayAutoFinishUntilSignal = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : false;
  // eslint-disable-next-line deprecation/deprecation
  var client = hub.getClient();
  var options = client && client.getOptions() || {}; // eslint-disable-next-line deprecation/deprecation

  var transaction = new IdleTransaction(transactionContext, hub, idleTimeout, finalTimeout, heartbeatInterval, onScope, delayAutoFinishUntilSignal);
  transaction = sampleTransaction(transaction, options, _objectSpread({
    name: transactionContext.name,
    parentSampled: transactionContext.parentSampled,
    transactionContext: transactionContext,
    attributes: _objectSpread(_objectSpread({}, transactionContext.data), transactionContext.attributes)
  }, customSamplingContext));

  if (transaction.isRecording()) {
    transaction.initSpanRecorder(options._experiments && options._experiments.maxSpans);
  }

  if (client && client.emit) {
    client.emit('startTransaction', transaction);
  }

  return transaction;
}
/**
 * Adds tracing extensions to the global hub.
 */


function addTracingExtensions() {
  var carrier = getMainCarrier();

  if (!carrier.__SENTRY__) {
    return;
  }

  carrier.__SENTRY__.extensions = carrier.__SENTRY__.extensions || {};

  if (!carrier.__SENTRY__.extensions.startTransaction) {
    carrier.__SENTRY__.extensions.startTransaction = _startTransaction;
  }

  if (!carrier.__SENTRY__.extensions.traceHeaders) {
    carrier.__SENTRY__.extensions.traceHeaders = traceHeaders;
  }

  registerErrorInstrumentation();
}

export { addTracingExtensions, startIdleTransaction };